/**
 * ChatResponse — Structured response from the deterministic-first pipeline.
 *
 * Every field except mlResponse and strongsAnalysis is deterministic.
 * The SLM does ONLY what it must: generate prose exposition.
 */
import { z } from 'zod';
import { QueryStatsSchema } from './query-stats.js';
// VariantAnalysis moved to model/bible/variant-analysis.ts (it's bible data,
// not an ML shape); re-exported here so existing imports keep working.
export { VariantAnalysisSchema } from '../bible/variant-analysis.js';
import { VariantAnalysisSchema } from '../bible/variant-analysis.js';
/** Pericope boundary — the passage section containing the queried verse */
export const PericopeSchema = z.object({
    book: z.string(),
    chapter: z.number(),
    startVerse: z.number(),
    endVerse: z.number(),
    title: z.string(),
    description: z.string().nullable(),
    summary: z.string().nullable(),
});
/** Deterministic content-validator findings on the model output.
 *  Populated by validateCommentaryOutput in the api chat route. Surfaced
 *  in the chat response so violations are visible during UAT; not yet
 *  used for retry/re-roll (that comes later if traffic warrants). */
export const ContentValidationSchema = z.object({
    hasViolations: z.boolean(),
    unlistedStrongs: z.array(z.string()),
    unlistedCrossRefs: z.array(z.string()),
    duplicateParagraphs: z.array(z.string()),
    orphanQuotes: z.array(z.string()).default([]),
    /** Fabricated quotes the deterministic stripper removed before send. */
    fabricatedQuotes: z.array(z.string()).default([]),
    /** Cross-ref allow-list floor — true when the model used zero refs from the list. */
    missingCrossRefFloor: z.boolean().default(false),
    /** Strong's numbers in the body that don't resolve to a real lemma. */
    badStrongs: z.array(z.string()).default([]),
    /** Stock closer phrases the deterministic detector flagged. */
    stockClosers: z.array(z.string()).default([]),
    /** P4 — lexicon-format rubric. False when the body cites Strong's
     *  numbers without a properly-formed `native (*phonetic*)` block,
     *  OR when no lexicon entry appears at all and the allowed Strong's
     *  block was non-empty.
     *
     *  `lexiconFormatOk` scores the **raw model body** — i.e. what the
     *  model itself emitted. Use this for training analysis.
     *  `lexiconFormatOkPostReform` scores the **post-process body** that
     *  the user actually sees, after `reformatLexiconBlocks` has reshaped
     *  the model's variants into spec. Use this for UX quality. */
    lexiconFormatOk: z.boolean().default(true),
    lexiconFormatOkPostReform: z.boolean().default(true),
    /** Parallel to `missingCrossRefFloor` — scored against the post-process
     *  body after `enforceCrossRefFloor` has injected a `(See also ...)`
     *  block when the model used zero allow-list refs. Should always be
     *  false when the allow-list was non-empty; surfaces injection
     *  failures only. */
    missingCrossRefFloorPostInjection: z.boolean().default(false),
    /** Misattributed quotes — text adjacent to a `(Book Ch:V)` ref that
     *  matches a DIFFERENT verse when searched against KJV SQLite. Findings
     *  here trigger judge-round re-rolls. */
    misattributedQuotes: z.array(z.string()).default([]),
    /** V6.1 Non-Negotiable Guards — doctrinal violations found across all
     *  guard rounds (`"<guard-id>: <excerpt or judge reason>"`). A violation
     *  triggers regenerate-never-edit: re-ask up to 2 times, then
     *  refuse-and-redirect. Entries here with outcome 'clean' or
     *  'regenerated' are history — the SHIPPED body passed the guards. */
    guardViolations: z.array(z.string()).default([]),
    /** How the guard phase ended: clean first pass, clean after
     *  regeneration, or refused after re-asks exhausted. Absent when the
     *  guard phase didn't run (rejections, guard check unavailable). */
    guardOutcome: z.enum(['clean', 'regenerated', 'refused']).optional(),
});
/** LLM-judge findings on the model output. Runs alongside the deterministic
 *  validator. Catches failure modes regex can't see: fake Greek/Hebrew native
 *  script not in the lexicon, semantically-wrong factual claims, fabricated
 *  quoted text that has no nearby reference. v1 surfaces only — no retry yet. */
export const ContentJudgmentSchema = z.object({
    verdict: z.enum(['pass', 'fail']),
    /** One-line plain-text reason for the verdict. Optional because the
     *  judge model (gemma3:4b) sometimes omits it on the "pass" path —
     *  treating it as required caused spurious Zod parse failures even
     *  when the verdict itself was valid. */
    reason: z.string().optional(),
    durationMs: z.number().optional(),
    /** Number of judge rounds that ran. 1 = passed first try. Up to 3 max
     *  (after which the response is replaced with a rejection notification). */
    rounds: z.number().optional(),
});
/** Doctrinal guard judge verdict (V6.1 Non-Negotiable Guards). Runs on
 *  llama3.2 after the deterministic guard pre-pass; catches semantic
 *  drift the regexes can't see (Spirit-as-energy without a red-flag
 *  keyword). Fail → regenerate, never edit. */
export const GuardJudgmentSchema = z.object({
    verdict: z.enum(['pass', 'fail']),
    /** The violated guard's id when the judge can name it. llama3.2
     *  sometimes omits or misspells it — nullable, never load-bearing. */
    guardId: z.string().nullable().optional(),
    reason: z.string().optional(),
    durationMs: z.number().optional(),
});
/** Raw extraction the llama3.2 guard judge returns for ONE review
 *  sentence — what the guard's subject is "identified as". The verdict
 *  itself is computed deterministically from guard data (llama3.2
 *  extracts faithfully but cannot classify doctrine — probe 2026-07-05). */
export const GuardExtractionSchema = z.object({
    identifiedAs: z.array(z.string()).default([]),
});
/** Full structured pipeline response — deterministic-first architecture */
export const PipelineResponseSchema = z.object({
    itIsWritten: z.string(),
    reference: z.string(),
    mlResponse: z.string(),
    /** The model's body before any deterministic post-process (no "It is
     *  written" opener, no KJV-quote inlining after cross-refs). Use this
     *  field for training analysis — `mlResponse` is the assembled response
     *  the user sees and contains server-injected text. */
    rawModelBody: z.string().optional(),
    /** The prompt sent to the model for this response. Carried to the report
     *  dialog so training analysis reads the input beside the output. */
    prompt: z.string().optional(),
    strongsAnalysis: z.string().nullable(),
    textualVariant: VariantAnalysisSchema.nullable(),
    crossReferences: z.array(z.string()),
    pericope: PericopeSchema.nullable(),
    validation: ContentValidationSchema.optional(),
    judgment: ContentJudgmentSchema.optional(),
    stats: QueryStatsSchema.optional(),
});
//# sourceMappingURL=chat-response.js.map